import { createAsyncThunk } from '@reduxjs/toolkit';

import type { RootState } from '@/store';

import type {
  BotFlowsElegiveisPage,
  BotFlowsElegiveisQuery,
  InternalChatActiveListResult,
  InternalChatConversa,
  InternalChatConversaDetails,
  InternalChatLinha,
  ListConversationsParams,
} from '../../types';
import { botFlowsElegiveisQueryKey } from '../../utils/botFlowsElegiveis';
import { conversationsService } from './services';

const reject = (error: unknown): string =>
  error instanceof Error ? error.message : 'Erro desconhecido';

export const fetchConversations = createAsyncThunk<
  InternalChatActiveListResult,
  ListConversationsParams,
  { rejectValue: string }
>('chatAtendimento/conversations/fetch', async (params, { rejectWithValue }) => {
  try {
    return await conversationsService.list(params);
  } catch (error) {
    return rejectWithValue(reject(error));
  }
});

export const loadMoreConversations = createAsyncThunk<
  InternalChatActiveListResult,
  ListConversationsParams,
  { rejectValue: string }
>('chatAtendimento/conversations/loadMore', async (params, { rejectWithValue }) => {
  try {
    return await conversationsService.list(params);
  } catch (error) {
    return rejectWithValue(reject(error));
  }
});

export const fetchConversationDetails = createAsyncThunk<
  InternalChatConversaDetails,
  string | { conversaId: string; includeJuridico?: boolean; includeSecondary?: boolean },
  { rejectValue: string }
>('chatAtendimento/conversations/details', async (arg, { rejectWithValue }) => {
  try {
    const conversaId = typeof arg === 'string' ? arg : arg.conversaId;
    const options =
      typeof arg === 'string'
        ? undefined
        : { includeJuridico: arg.includeJuridico, includeSecondary: arg.includeSecondary };
    return await conversationsService.details(conversaId, options);
  } catch (error) {
    return rejectWithValue(reject(error));
  }
});

export const archiveConversation = createAsyncThunk<
  InternalChatConversa,
  string,
  { rejectValue: string }
>('chatAtendimento/conversations/archive', async (conversaId, { rejectWithValue }) => {
  try {
    return await conversationsService.close(conversaId);
  } catch (error) {
    return rejectWithValue(reject(error));
  }
});

export const unarchiveConversation = createAsyncThunk<
  InternalChatConversa,
  string,
  { rejectValue: string }
>('chatAtendimento/conversations/unarchive', async (conversaId, { rejectWithValue }) => {
  try {
    return await conversationsService.unarchive(conversaId);
  } catch (error) {
    return rejectWithValue(reject(error));
  }
});

export const transferConversation = createAsyncThunk<
  InternalChatConversa,
  { conversaId: string; novoResponsavelId: string; motivo?: string },
  { rejectValue: string }
>(
  'chatAtendimento/conversations/transfer',
  async ({ conversaId, novoResponsavelId, motivo }, { rejectWithValue }) => {
    try {
      return await conversationsService.transfer(conversaId, { novoResponsavelId, motivo });
    } catch (error) {
      return rejectWithValue(reject(error));
    }
  },
);

export const assignConversationResponsavel = createAsyncThunk<
  InternalChatConversa,
  { conversaId: string; responsavelId: string | null },
  { rejectValue: string }
>(
  'chatAtendimento/conversations/assignResponsavel',
  async ({ conversaId, responsavelId }, { rejectWithValue }) => {
    try {
      return await conversationsService.assignResponsavel(conversaId, responsavelId);
    } catch (error) {
      return rejectWithValue(reject(error));
    }
  },
);

const EXPORT_POLL_INTERVAL_MS = 2000;
const EXPORT_POLL_MAX_ATTEMPTS = 90;

const sleep = (ms: number): Promise<void> => new Promise((resolve) => setTimeout(resolve, ms));

const triggerDownload = (url: string, conversaId: string): void => {
  const a = document.createElement('a');
  a.href = url;
  a.target = '_blank';
  a.rel = 'noopener';
  a.download = `conversa_${conversaId}.pdf`;
  document.body.appendChild(a);
  a.click();
  a.remove();
};

export const exportConversationPdf = createAsyncThunk<
  { url: string },
  string,
  { rejectValue: string }
>('chatAtendimento/conversations/exportPdf', async (conversaId, { rejectWithValue }) => {
  try {
    const { jobId } = await conversationsService.enqueueExportPdf(conversaId);
    for (let attempt = 0; attempt < EXPORT_POLL_MAX_ATTEMPTS; attempt += 1) {
      const status = await conversationsService.getExportPdfStatus(conversaId, jobId);
      if (status.status === 'READY') {
        triggerDownload(status.url, conversaId);
        return { url: status.url };
      }
      if (attempt < EXPORT_POLL_MAX_ATTEMPTS - 1) {
        await sleep(EXPORT_POLL_INTERVAL_MS);
      }
    }
    return rejectWithValue('Tempo limite ao gerar o PDF. Tente novamente.');
  } catch (error) {
    return rejectWithValue(reject(error));
  }
});

export const assumirAtendimento = createAsyncThunk<
  InternalChatConversa,
  { conversaId: string; reason?: string },
  { rejectValue: string }
>('chatAtendimento/conversations/assumir', async ({ conversaId, reason }, { rejectWithValue }) => {
  try {
    return await conversationsService.assumir(conversaId, reason);
  } catch (error) {
    return rejectWithValue(reject(error));
  }
});

export const retomarBot = createAsyncThunk<InternalChatConversa, string, { rejectValue: string }>(
  'chatAtendimento/conversations/retomarBot',
  async (conversaId, { rejectWithValue }) => {
    try {
      return await conversationsService.retomarBot(conversaId);
    } catch (error) {
      return rejectWithValue(reject(error));
    }
  },
);

export const fetchLinhas = createAsyncThunk<InternalChatLinha[], void, { rejectValue: string }>(
  'chatAtendimento/conversations/fetchLinhas',
  async (_, { rejectWithValue }) => {
    try {
      return await conversationsService.listLinhas();
    } catch (error) {
      return rejectWithValue(reject(error));
    }
  },
);

export const updateLinha = createAsyncThunk<
  InternalChatLinha,
  { id: string; patch: { botFlowId?: string | null; autoAtendimento?: boolean } },
  { rejectValue: string }
>('chatAtendimento/conversations/updateLinha', async ({ id, patch }, { rejectWithValue }) => {
  try {
    return await conversationsService.updateLinha(id, patch);
  } catch (error) {
    return rejectWithValue(reject(error));
  }
});

export const fetchBotFlowsElegiveis = createAsyncThunk<
  BotFlowsElegiveisPage,
  BotFlowsElegiveisQuery,
  { state: Pick<RootState, 'chatAtendimentoConversations'>; rejectValue: string }
>(
  'chatAtendimento/conversations/fetchBotFlowsElegiveis',
  async (query, { rejectWithValue }) => {
    try {
      return await conversationsService.listBotFlowsElegiveis(query);
    } catch (error) {
      return rejectWithValue(reject(error));
    }
  },
  {
    // Cada linha monta o próprio select: sem isso, N linhas disparariam N vezes a mesma página.
    condition: (query, { getState }) =>
      getState().chatAtendimentoConversations.botFlowsElegiveis[botFlowsElegiveisQueryKey(query)]
        ?.status !== 'loading',
  },
);
